import { FormDatepickerField, FormSelect } from 'components/forms';
import { useCallback, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useSubmitNew from 'shared/hooks/useSubmitNew';
import MassActionModalLayout from 'shared/layouts/massActionModalLayout';
import { MassActionItemProps, UseMassActionProps } from 'shared/types';
import { oneYearAheadToCurrentDate } from 'shared/utility';
import { ActivePriceMassActionForm } from 'types/backend';

type ChangeActivePriceDeviationDateActionForm = { endDate: string; comments: string };
export type ChangeActivePriceDeviationDateForm = ActivePriceMassActionForm<ChangeActivePriceDeviationDateActionForm>;

type ChangeDeviationDateProps<TActivePrice> = MassActionItemProps<
    ChangeActivePriceDeviationDateForm,
    TActivePrice,
    ChangeActivePriceDeviationDateActionForm
>;

enum ReasonEnum {
    EXTEND = 'Extend',
    EXPIRE = 'Expire',
}

const reasonOptions = [
    {
        value: ReasonEnum.EXTEND,
        displayText: ReasonEnum.EXTEND,
        original: ReasonEnum.EXTEND,
    },
    {
        value: ReasonEnum.EXPIRE,
        displayText: ReasonEnum.EXPIRE,
        original: ReasonEnum.EXPIRE,
    },
];

export default function ChangeDeviationDateActivePrices<TActivePrice>({
    defaultValues,
    onSubmit,
    isLoading,
    tableDataContext,
    rowCount,
    columnConfig,
    allRowsSelected,
    validate,
}: ChangeDeviationDateProps<TActivePrice>) {
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = useForm<ChangeActivePriceDeviationDateForm>({
        defaultValues,
    });
    const reason = watch('actionForm.comments');

    const setEndDate = useCallback(
        (reason) => {
            const now = new Date().toLocaleDateString();
            if (reason === ReasonEnum.EXTEND) {
                setValue('actionForm.endDate', oneYearAheadToCurrentDate());
            }
            if (reason === ReasonEnum.EXPIRE) {
                setValue('actionForm.endDate', now);
            }
        },
        [setValue]
    );

    useEffect(() => {
        setEndDate(reason);
    }, [reason, setEndDate]);

    return (
        <MassActionModalLayout
            isLoading={isLoading}
            tableDataContext={tableDataContext}
            columnConfig={columnConfig}
            allRowsSelected={allRowsSelected}
            rowCount={rowCount}
            submitHandler={handleSubmit(onSubmit)}
        >
            <FormSelect
                name={'actionForm.comments'}
                label='Reason'
                control={control}
                options={reasonOptions}
                rules={{
                    required: 'Reason is required',
                }}
                fieldError={errors?.['actionForm']?.['comments']}
            />
            <FormDatepickerField name={'actionForm.endDate'} label='End Date' control={control} disabled />
        </MassActionModalLayout>
    );
}

export function useChangeDeviationDateActivePriceMassAction<TActivePrice>({
    controllerName,
    affectedDataContext,
    selected,
    allRowsSelected,
    onSuccess,
}: UseMassActionProps<TActivePrice>) {
    const { filtersApplied } = useContext(affectedDataContext);

    const changeDeviationDateMapper = (form: ChangeActivePriceDeviationDateForm) => ({
        ...form,
        filters: filtersApplied,
        selected,
        allRowsSelected,
        type: 'Change active price deviation end date',
    });

    return useSubmitNew({
        name: `${controllerName}/massaction/changedeviationdate`,
        mapper: changeDeviationDateMapper,
        context: affectedDataContext,
        onSuccess: onSuccess,
    });
}
